/**
 * The active workspace.
 *
 * Every page reads the current business from here instead of carrying it in the URL: switching
 * businesses should not invalidate a bookmarked document. The choice is remembered per browser,
 * and falls back to the first workspace the user is a member of.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { canManage, canWrite, roleIn, type MemberRole, type Workspace } from "@/lib/api/workspaces";
import { useAuth } from "@/lib/session/auth-context";
import { useMyMemberships, useWorkspaces } from "@/hooks/use-workspaces";
import { useWorkspaceSubscription } from "@/hooks/use-billing";
import { limitsFor, planState, type Limits, type PlanState } from "@/lib/plan/plan";

const STORAGE_KEY = "workspace";

type WorkspaceValue = {
  workspaces: Workspace[];
  workspace: Workspace | null;
  workspaceId: string;
  role: MemberRole | null;
  /**
   * May change data: the role allows it (owner, member - not accountant, viewer) and the business
   * is not read-only for want of a plan.
   */
  canWrite: boolean;
  /** Trial, paid plan or read-only; null until the subscription has loaded. */
  plan: PlanState | null;
  /** What the plan allows; null while read-only or loading. */
  limits: Limits | null;
  /** May change settings and manage members (owner). */
  canManage: boolean;
  loading: boolean;
  /** True once loading finished and the user has no workspace yet. */
  needsOnboarding: boolean;
  select: (id: string) => void;
};

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

function readStored(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const {
    data: workspaces,
    isPending: loadingWorkspaces,
    isFetching: fetchingWorkspaces,
  } = useWorkspaces(isAuthenticated);
  const { data: memberships, isPending: loadingMemberships } = useMyMemberships(user?.id);
  const [selected, setSelected] = useState<string>(readStored);

  const list = useMemo(
    () => (workspaces ?? []).filter((workspace) => !workspace.archived),
    [workspaces],
  );
  // A stored id can point at a workspace the user has left; the first one is then the fallback.
  const workspace = list.find((entry) => entry.id === selected) ?? list[0] ?? null;

  const select = useCallback((id: string) => {
    setSelected(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // A browser with storage disabled just forgets the choice between visits.
    }
  }, []);

  const { data: subscription, isPending: loadingPlan } = useWorkspaceSubscription(workspace?.id);
  const plan = useMemo(
    () =>
      workspace && !loadingPlan
        ? planState({ trialEndsAt: workspace.trialEndsAt, subscription })
        : null,
    [workspace, subscription, loadingPlan],
  );

  const role = workspace ? roleIn(memberships ?? [], workspace.id) : null;
  // The owner always has full rights even before the membership query lands.
  const effectiveRole: MemberRole | null =
    role ?? (workspace && user && workspace.owner === user.id ? "owner" : null);
  // An empty list that is being fetched again (right after accepting an invitation, say) is not
  // an answer yet: deciding on it would send a new member to "create a business".
  const loading =
    isAuthenticated &&
    (loadingWorkspaces || loadingMemberships || (list.length === 0 && fetchingWorkspaces));

  const value = useMemo<WorkspaceValue>(
    () => ({
      workspaces: list,
      workspace,
      workspaceId: workspace?.id ?? "",
      role: effectiveRole,
      canWrite: canWrite(effectiveRole) && plan?.kind !== "expired",
      plan,
      limits: plan ? limitsFor(plan) : null,
      canManage: canManage(effectiveRole),
      loading,
      needsOnboarding: isAuthenticated && !loading && list.length === 0,
      select,
    }),
    [list, workspace, effectiveRole, plan, loading, isAuthenticated, select],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace(): WorkspaceValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return context;
}
